import { Skeleton } from '@pique/component';
import { ResizePanel } from '@pique/component/resize-panel';
import { openSettingModalAtom } from '@pique/core/atoms';
import { DEFAULT_SETTING_TAB } from '@pique/core/components/pique/setting-modal/types';
import { AccountSetting03Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { debounce } from 'lodash-es';
import type { PropsWithChildren, ReactElement } from 'react';
import { useCallback, useEffect } from 'react';

import {
  fallbackFooterStyle,
  fallbackHeaderStyle,
  fallbackSettingsButtonStyle,
  fallbackSettingsLabelStyle,
  fallbackStyle,
} from './fallback.css';
import {
  floatingMaxWidth,
  navBodyStyle,
  navHeaderStyle,
  navStyle,
  navWrapperStyle,
  sidebarFloatMaskStyle,
} from './index.css';
import {
  APP_SIDEBAR_OPEN,
  appSidebarFloatingAtom,
  appSidebarOpenAtom,
  appSidebarResizingAtom,
  appSidebarWidthAtom,
  isMobileView,
} from './index.jotai';
import { SidebarHeader } from './sidebar-header';

export type AppSidebarProps = PropsWithChildren<{
  clientBorder?: boolean;
  translucentUI?: boolean;
}>;

export type History = {
  stack: string[];
  current: number;
};

// Pique Update
export const MAX_WIDTH = 680;
export const CUTOFF_WIDTH = 200;
export const MIN_WIDTH = 85;

export function AppSidebar({
  children,
  clientBorder,
}: AppSidebarProps): ReactElement {
  const [open, setOpen] = useAtom(appSidebarOpenAtom);
  const [width, setWidth] = useAtom(appSidebarWidthAtom);
  const [floating, setFloating] = useAtom(appSidebarFloatingAtom);
  const [resizing, setResizing] = useAtom(appSidebarResizingAtom);

  useEffect(() => {
    function onResize() {
      const isFloatingMaxWidth = window.matchMedia(
        `(max-width: ${floatingMaxWidth}px)`
      ).matches;
      const isOverflowWidth = window.matchMedia(
        `(max-width: ${width / 0.4}px)`
      ).matches;
      const isFloating = isFloatingMaxWidth || isOverflowWidth;
      if (
        open === undefined &&
        localStorage.getItem(APP_SIDEBAR_OPEN) === null
      ) {
        // give the initial value,
        // so that the sidebar can be closed on mobile by default
        setOpen(!isFloating);
      }
      setFloating(isFloating);
    }

    const dOnResize = debounce(onResize, 50);
    window.addEventListener('resize', dOnResize);
    return () => {
      window.removeEventListener('resize', dOnResize);
    };
  }, [open, setFloating, setOpen, width]);

  const hasRightBorder = !clientBorder;
  return (
    <>
      <ResizePanel
        floating={floating}
        open={open}
        resizing={resizing}
        maxWidth={MAX_WIDTH}
        minWidth={MIN_WIDTH}
        width={width}
        resizeHandlePos="right"
        onOpen={setOpen}
        onResizing={setResizing}
        onWidthChange={setWidth}
        className={navWrapperStyle}
        resizeHandleOffset={clientBorder ? 8 : 0}
        resizeHandleVerticalPadding={clientBorder ? 16 : 0}
        data-transparent
        data-open={open}
        data-has-border={hasRightBorder}
        data-testid="app-sidebar-wrapper"
        data-client-border={clientBorder}
        data-is-minimum-width={width <= CUTOFF_WIDTH}
      >
        <nav className={navStyle} data-testid="app-sidebar">
          <SidebarHeader />
          <div className={navBodyStyle} data-testid="sliderBar-inner">
            {children}
          </div>
        </nav>
      </ResizePanel>
      <div
        data-testid="app-sidebar-float-mask"
        data-open={open}
        data-is-floating={floating}
        className={sidebarFloatMaskStyle}
        onClick={() => setOpen(false)}
      />
    </>
  );
}

export const AppSidebarFallback = (): ReactElement | null => {
  const isMobileViewValue = useAtomValue(isMobileView);
  const width = useAtomValue(appSidebarWidthAtom);
  const isMinimumWidth = width <= CUTOFF_WIDTH;
  const setOpenSettingModalAtom = useSetAtom(openSettingModalAtom);

  const onOpenSettings = useCallback(() => {
    setOpenSettingModalAtom({
      activeTab: DEFAULT_SETTING_TAB,
      open: true,
    });
  }, [setOpenSettingModalAtom]);

  if (isMobileViewValue) {
    return null;
  }

  return (
    <div
      style={{ width, position: 'relative' }}
      className={navWrapperStyle}
      data-has-border
      data-open="true"
      data-is-minimum-width={isMinimumWidth}
    >
      <nav className={navStyle}>
        <div className={navHeaderStyle} data-open="true" />
        <div className={navBodyStyle}>
          <div className={fallbackStyle}>
            <div className={fallbackHeaderStyle}>
              <Skeleton variant="circular" width={40} height={40} />
              <Skeleton variant="rectangular" width={150} height={40} />
            </div>
          </div>
        </div>
        <div className={fallbackFooterStyle}>
          <button
            className={fallbackSettingsButtonStyle}
            onClick={onOpenSettings}
            title="Settings"
          >
            <HugeiconsIcon
              icon={AccountSetting03Icon}
              size={16}
              strokeWidth={1.5}
              style={{ display: 'block', flexShrink: 0 }}
            />
            <span className={fallbackSettingsLabelStyle}>Settings</span>
          </button>
        </div>
      </nav>
    </div>
  );
};

export * from './category-divider';
export * from './index.css';
export * from './menu-item';
export * from './quick-search-input';
export * from './sidebar-containers';
export * from './sidebar-header';
export { appSidebarFloatingAtom, appSidebarOpenAtom, appSidebarResizingAtom };
